import { timeUtils } from '@sato/components';
import { action, computed, makeObservable, observable } from 'mobx';
import { create, persist } from 'mobx-persist';

import { parkingApi } from '@api/parking.api';
import {
  ConfirmParkingChangeAgreementRequest,
  ParkingProduct,
} from '@models/parking.types';
import { getDatesWhenParkingSpacesCanBeSwitched } from '@views/parking/parking.utils';

import { contractStore } from '../contractStore';

import { ParkingStoreBase } from './parkingStoreBase';

type ParkingProductSubmitResult = 'SUCCESS' | 'STRONG_AUTH_REQUIRED';

/** Stores data related to changing from a parking space to a new one */
export class SwitchParkingSpacesStore extends ParkingStoreBase {
  /** The parking contract user wants to change from */
  @persist currentParkingContractId: string | null = null;

  /** Data the user submitted. Data is referred to in the summary page. */
  submittedData: ConfirmParkingChangeAgreementRequest | null = null;

  setCurrentParkingContractId = (newValue: string) => {
    this.currentParkingContractId = newValue;
  };

  constructor() {
    super();
    // NOTE: use makeObservable instead of makeAutoObservable since the latter doesn't work when using class inheritance
    makeObservable(this, {
      // from this class
      currentParkingContractId: observable,
      submittedData: observable,
      createOrUpdateDraftAgreement: action,
      submitAgreement: action,

      // from inherited ParkingStoreBase class
      draftAgreementId: observable,
      lockEndDate: observable,
      lockedParkingProduct: observable,
      agreementConfirmationResponse: observable,
      hasOngoingLock: computed,
      abortRentingAndUnlockProduct: action,
      clear: action,
      startSwitchingProcess: action,
    });
  }

  /** Locks the product and sets the parking contract id the user is changing from. */
  async startSwitchingProcess(
    parkingProduct: ParkingProduct,
    parkingContractIdToChangeFrom: string
  ) {
    this.setCurrentParkingContractId(parkingContractIdToChangeFrom);

    return this.lockProduct(parkingProduct);
  }

  /** Creates a new draft agreement */
  async createOrUpdateDraftAgreement() {
    if (!this.lockedParkingProduct)
      throw Error('lockedParkingProduct is required');
    if (!contractStore.selectedContractId)
      throw Error('selectedContractId is required');
    if (!this.currentParkingContractId)
      throw Error('currentParkingContractId is required');

    const { data } = await (this.draftAgreementId
      ? // Just download the draft agreement pdf if it was previously created
        parkingApi.updateParkingChangeAgreementDraft({
          offerId: this.draftAgreementId,
        })
      : // Create a new parking agreement draft
        parkingApi.createParkingChangeAgreementDraft({
          productId: this.lockedParkingProduct.id,
          startDate: timeUtils.formatISO(
            getDatesWhenParkingSpacesCanBeSwitched().newContractStartDate,
            { representation: 'date' }
          ),
          apartmentContractId: contractStore.selectedContractId,
          // This is the parking contract user wants to terminate (change from)
          parkingContractId: this.currentParkingContractId,
        }));

    this.draftAgreementId = data.offerId;

    return data;
  }

  /** Submits the parking space switch agreement. Clears the state and sets submittedForm if successful. */
  async submitAgreement(): Promise<ParkingProductSubmitResult> {
    if (!this.lockedParkingProduct)
      throw Error('lockedParkingProduct is required');
    if (!contractStore.selectedContractId)
      throw Error('selectedContractId is required');
    if (!this.currentParkingContractId)
      throw Error('currentParkingContractId is required');

    // // Create or update the draft agreement to make sure it exists and is up to date with form values
    const { offerId, strongAuthenticationRequired } =
      await this.createOrUpdateDraftAgreement();

    if (strongAuthenticationRequired) return 'STRONG_AUTH_REQUIRED';

    const possibleContractDates = getDatesWhenParkingSpacesCanBeSwitched();

    const serializedStartDate = timeUtils.formatISO(
      possibleContractDates.newContractStartDate,
      { representation: 'date' }
    );

    const serializedEndDate = timeUtils.formatISO(
      possibleContractDates.oldContractEndDate,
      { representation: 'date' }
    );

    const requestData: ConfirmParkingChangeAgreementRequest = {
      offerId,
      startDate: serializedStartDate,
      apartmentContractId: contractStore.selectedContractId,
      // This is the parking contract user wants to terminate (change from)
      parkingContractId: this.currentParkingContractId,
    };

    const response =
      await parkingApi.confirmParkingChangeAgreement(requestData);

    // Optimistically add a parking contract to contract store. Creating a contract takes around a minute
    // in the backend so we can't fetch the contract information from API at this point.
    contractStore.addQuickRentedParkingContractFromParkingProduct({
      parkingProduct: this.lockedParkingProduct,
      contractId: offerId,
      contractStartingDate: serializedStartDate,
    });

    // Optimistically set the old contract as terminated.
    contractStore.setParkingSpaceContractAsTerminated({
      contractEndDate: serializedEndDate,
      contractId: this.currentParkingContractId,
    });

    this.agreementConfirmationResponse = response.data;

    this.draftAgreementId = null;
    // Clear lock end date but keep lockedParkingProduct so that we can show it in summary page
    this.lockEndDate = null;
    // Set submitted data so that we can refer to it in summary page
    this.submittedData = requestData;

    return 'SUCCESS';
  }

  override clear() {
    super.clear();
    // Clear store values
    this.currentParkingContractId = null;
    this.submittedData = null;
  }
}

export const switchParkingSpacesStore = new SwitchParkingSpacesStore();

create()('switch-parking-spaces-store', switchParkingSpacesStore);
